import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import imageCompression from 'browser-image-compression';
import { ThemeContext } from '../../contexts/ThemeContext';

import { authFetch } from '../../services/authFetch';

const KersosForm = ({ onClose, initialData = null, readOnly = false, status = null, statusText = null, isEdit = false, onSubmit = null }) => {
  const { darkMode } = useContext(ThemeContext);
  const [formData, setFormData] = useState({
    kersos_date: '',
    start_time: '',
    finish_time: '',
    type_or_work: '',
    reflection: '',
    pic: '',
    pic_contact_number: '',
    images: []
  });
  const [reflectionCharCount, setReflectionCharCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [imageValidation, setImageValidation] = useState({ 
    hasValidImages: false, 
    errorMessage: '', 
    isPristine: true 
  });

  // Load initial data if provided
  useEffect(() => {
    if (initialData) {
      // Make sure images is properly initialized from API response
      const images = initialData.images || [];
      setFormData({
        kersos_date: initialData.kersos_date || '',
        start_time: initialData.start_time || '',
        finish_time: initialData.finish_time || '',
        type_or_work: initialData.type_or_work || '',
        reflection: initialData.reflection || '',
        pic: initialData.pic || '',
        pic_contact_number: initialData.pic_contact_number || '',
        images
      });
      setReflectionCharCount((initialData.reflection || '').length);
    }
  }, [initialData]);

  // Validate images whenever they change
  useEffect(() => {
    const hasImages = formData.images && formData.images.length > 0;
    setImageValidation({
      hasValidImages: hasImages,
      errorMessage: hasImages ? '' : 'Foto bukti kegiatan wajib diunggah (minimal 1 foto).',
      isPristine: false
    });
  }, [formData.images]);

  const handleFileChange = async (e) => {
    if (readOnly) return;
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length !== files.length) {
      setNotification({ show: true, message: 'Hanya file gambar yang diperbolehkan.', type: 'error' });
      return;
    }

    try {
      const compressedFiles = await Promise.all(
        imageFiles.map(async (file) => {
          try {
            // Mobile-optimized compression settings
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            const options = {
              maxSizeMB: isMobile ? 0.3 : 0.5, // Smaller size for mobile
              maxWidthOrHeight: isMobile ? 800 : 1024, // Lower resolution for mobile
              useWebWorker: true,
              fileType: 'image/jpeg', // Force JPEG for better compression
              initialQuality: 0.8
            };
            
            let compressedFile;
            if (file.size > (isMobile ? 512 * 1024 : 1024 * 1024)) {
              compressedFile = await imageCompression(file, options);
              
              // Fallback compression if still too large
              if (compressedFile.size > (isMobile ? 300 * 1024 : 500 * 1024)) {
                const fallbackOptions = {
                  ...options,
                  maxSizeMB: isMobile ? 0.2 : 0.3,
                  maxWidthOrHeight: isMobile ? 600 : 800,
                  initialQuality: 0.6
                };
                compressedFile = await imageCompression(compressedFile, fallbackOptions);
              }
            } else {
              compressedFile = file;
            }
            
            // Ensure the result is a proper File object, not a Blob
            if (compressedFile instanceof Blob && !(compressedFile instanceof File)) {
              const fileName = file.name || `compressed_image_${Date.now()}.jpg`;
              compressedFile = new File([compressedFile], fileName, {
                type: compressedFile.type || 'image/jpeg',
                lastModified: Date.now()
              });
            }
            
            return compressedFile;
          } catch (compressionError) {
            console.error('Error during image compression:', compressionError);
            // Return original file if compression fails
            return file;
          }
        })
      );

      const validFiles = compressedFiles.filter(file => file !== null);
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...validFiles].slice(0, 5) // Limit to 5 images
      }));
      
      // Clear the input to allow re-selecting the same file
      e.target.value = '';
      
    } catch (error) {
      console.error('Error processing images:', error);
      setNotification({ 
        show: true, 
        message: 'Gagal memproses gambar. Silakan coba lagi.', 
        type: 'error' 
      });
    }
  };

  const handleChange = (e) => {
    if (readOnly) return;
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      // now handled by handleFileChange
    } else if (name === 'reflection') {
      setFormData({ ...formData, reflection: value });
      setReflectionCharCount(value.length);
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) {
      onClose();
      return;
    }
    
    // If this is edit mode and onSubmit callback is provided, use it instead of internal logic
    if (isEdit && onSubmit) {
      // Validate images for edit mode
      if (!imageValidation.hasValidImages) {
        setNotification({ show: true, message: imageValidation.errorMessage, type: 'error' });
        return;
      }
      
      setLoading(true);
      try {
        await onSubmit(formData);
        // Don't close the modal here - let the parent handle it after successful update
      } catch (error) {
        setNotification({ show: true, message: error.message || 'Gagal memperbarui laporan.', type: 'error' });
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // Original create logic
    setNotification({ show: false, message: '', type: 'success' });
    setLoading(true);
    // Require at least one photo evidence
    if (!formData.images || formData.images.length === 0) {
      setNotification({ show: true, message: 'Foto bukti kegiatan wajib diunggah (minimal 1 foto).', type: 'error' });
      setLoading(false);
      return;
    }
    if (reflectionCharCount < 200) {
      setNotification({ show: true, message: 'Refleksi harus minimal 200 karakter.', type: 'error' });
      setLoading(false);
      return;
    }
    
    // Check for cross-midnight scenarios and warn user
    if (formData.start_time && formData.finish_time) {
      const [startHour, startMinute] = formData.start_time.split(':').map(Number);
      const [finishHour, finishMinute] = formData.finish_time.split(':').map(Number);
      const startTotalMinutes = startHour * 60 + startMinute;
      const finishTotalMinutes = finishHour * 60 + finishMinute;
      
      // If finish time is earlier than start time, it's a cross-midnight scenario
      if (finishTotalMinutes <= startTotalMinutes) {
        setNotification({ 
          show: true, 
          message: 'Waktu selesai tidak boleh lebih awal dari waktu mulai. Untuk kegiatan lintas tengah malam, silakan buat laporan terpisah.', 
          type: 'error' 
        });
        setLoading(false);
        return;
      }
    }
    
    const dataToSend = new FormData();
    dataToSend.append('kersos_date', formData.kersos_date);
    dataToSend.append('start_time', formData.start_time);
    dataToSend.append('finish_time', formData.finish_time);
    dataToSend.append('type_or_work', formData.type_or_work);
    dataToSend.append('reflection', formData.reflection);
    dataToSend.append('pic', formData.pic);
    dataToSend.append('pic_contact_number', formData.pic_contact_number);
    if (formData.images && formData.images.length > 0) {
      formData.images.forEach((file) => {
        // Ensure we're sending File objects, not Blobs
        if (file instanceof Blob && !(file instanceof File)) {
          const fileName = `kersos_image_${Date.now()}.jpg`;
          const fileObject = new File([file], fileName, {
            type: file.type || 'image/jpeg',
            lastModified: Date.now()
          });
          dataToSend.append('images[]', fileObject);
        } else if (file instanceof File) {
          dataToSend.append('images[]', file);
        } else {
          console.warn('Invalid file type detected:', typeof file);
        }
      });
    }
    try {
      const response = await authFetch('/api/kersos_reports', {
        method: 'POST',
        body: dataToSend
      });
      const responseText = await response.clone().text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseErr) {
        setNotification({ show: true, message: 'Gagal mengurai respons server.', type: 'error' });
        setLoading(false);
        return;
      }
      if (!response.ok) {
        setNotification({ show: true, message: data.message || 'Gagal mengirim laporan kersos.', type: 'error' });
      } else {
        setNotification({ show: true, message: data.message || 'Laporan kersos berhasil dikirim!', type: 'success' });
        setFormData({
          kersos_date: '',
          start_time: '',
          finish_time: '',
          type_or_work: '',
          reflection: '',
          pic: '',
          pic_contact_number: '',
          images: []
        });
        setReflectionCharCount(0);
        if (onClose) {
          setTimeout(() => {
            onClose();
          }, 2000);
        }
      }
    } catch (err) {
      setNotification({ show: true, message: 'Terjadi kesalahan jaringan.', type: 'error' });
    }
    setLoading(false);
  };

  return (
    <FormWrapper theme={darkMode ? 'dark' : 'light'}>
      <FormHeader>
        <HeaderContent>
          <h2>{isEdit ? 'Edit Laporan Kersos' : 'Formulir Laporan Kersos'}</h2>
          {status && (
            <StatusBadge status={status}>
              {statusText}
            </StatusBadge>
          )}
        </HeaderContent>
        <CloseButton onClick={onClose}>×</CloseButton>
      </FormHeader>
      
      <FormContent>
        {notification.show && (
          <NotificationToast $type={notification.type}>
            <NotificationMessage>{notification.message}</NotificationMessage>
            <CloseNotificationButton onClick={() => setNotification({ ...notification, show: false })}>
              ×
            </CloseNotificationButton>
          </NotificationToast>
        )}
        <form onSubmit={handleSubmit}>
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="kersos_date">Tanggal Kersos</label>
            <input
              type="date"
              id="kersos_date"
              name="kersos_date"
              value={formData.kersos_date}
              onChange={handleChange}
              required
              readOnly={readOnly}
              disabled={readOnly}
            />
          </FormGroup>
          
          <FormRow>
            <FormGroup theme={darkMode ? 'dark' : 'light'}>
              <label htmlFor="start_time">Jam Mulai</label>
              <input
                type="time"
                id="start_time"
                name="start_time"
                value={formData.start_time}
                onChange={handleChange}
                required
                readOnly={readOnly}
                disabled={readOnly}
              />
            </FormGroup>
            <FormGroup theme={darkMode ? 'dark' : 'light'}>
              <label htmlFor="finish_time">Jam Selesai</label>
              <input
                type="time"
                id="finish_time"
                name="finish_time"
                value={formData.finish_time}
                onChange={handleChange}
                required
                readOnly={readOnly}
                disabled={readOnly}
              />
            </FormGroup>
          </FormRow>
          
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="type_or_work">Jenis Pekerjaan</label>
            <input
              type="text"
              id="type_or_work"
              name="type_or_work"
              value={formData.type_or_work || ''}
              onChange={handleChange}
              placeholder="Contoh: Membersihkan lingkungan, membantu guru, dll."
              required
              readOnly={readOnly}
              disabled={readOnly}
            />
          </FormGroup>
          
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="reflection">Refleksi</label>
            <textarea
              id="reflection"
              name="reflection"
              rows="4"
              value={formData.reflection}
              onChange={handleChange}
              placeholder="Tuliskan refleksi diri tentang kegiatan Kersos yang telah dilakukan. Min 200 kata."
              required
              readOnly={readOnly}
              disabled={readOnly}
            ></textarea>
            <div style={{ fontSize: '0.9em' }}>
              {`Jumlah karakter: ${reflectionCharCount} / 200`}
            </div>
          </FormGroup>
          
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="pic">Penanggung Jawab</label>
            <input
              type="text"
              id="pic"
              name="pic"
              value={formData.pic || ''}
              onChange={handleChange}
              placeholder="Masukkan nama penanggung jawab"
              required
              readOnly={readOnly}
              disabled={readOnly}
            />
          </FormGroup>
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="pic_contact_number">Nomor Telepon Penanggung Jawab</label>
            <input
              type="text"
              id="pic_contact_number"
              name="pic_contact_number"
              value={formData.pic_contact_number || ''}
              onChange={handleChange}
              placeholder="Masukkan nomor telepon"
              required
              readOnly={readOnly}
              disabled={readOnly}
              inputMode="numeric"
              maxLength={15}
            />
          </FormGroup>
          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="images">Foto bukti kegiatan, wajib dengan pendamping (bisa lebih dari 1 foto)</label>
            <input
              type="file"
              id="images"
              name="images"
              multiple
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleFileChange}
              disabled={readOnly}
            />
            <div style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginTop: '0.5rem' }}>
              Minimal 1 foto, maksimal 5 foto. Format: JPG, PNG, WebP. Ukuran per foto ≤ 2MB.
            </div>
            {!imageValidation.hasValidImages && !imageValidation.isPristine && (
              <ImageValidationError>{imageValidation.errorMessage}</ImageValidationError>
            )}
            {imageValidation.hasValidImages && (
              <ImageValidationSuccess>✓ {formData.images.length} foto terunggah</ImageValidationSuccess>
            )}
             <ImagePreviewContainer>
              {formData.images && formData.images.map((image, index) => {
                  // Handle different image formats
                  let imgSrc = '';
                if (image instanceof File) {
                  imgSrc = URL.createObjectURL(image);
                } else if (typeof image === 'string') {
                  imgSrc = image;
                } else if (image && image.image_url) {
                    // API returns image objects with image_url property
                    const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
                  imgSrc = image.image_url.startsWith('http') 
                    ? image.image_url 
                    : `${baseUrl}${image.image_url.startsWith('/') ? '' : '/'}${image.image_url}`;
                } else if (image && image.url) {
                  imgSrc = image.url;
                  }
                  
                  return (
                  <ImagePreview key={index}>
                    <img
                      src={imgSrc}
                      alt={`preview ${index}`}
                      onError={(e) => {
                        console.error('Image failed to load:', imgSrc);
                        e.target.style.display = 'none';
                      }}
                    />
                    {!readOnly && (
                      <RemoveImageButton 
                        onClick={() => {
                          // Only allow removal if there are more than 1 images
                          if (formData.images.length > 1) {
                            setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
                          } else {
                            setNotification({ show: true, message: 'Minimal 1 foto harus tersedia.', type: 'error' });
                          }
                        }}
                        disabled={formData.images.length <= 1}
                        title={formData.images.length <= 1 ? 'Minimal 1 foto harus tersedia' : 'Hapus foto'}
                      >
                        ×
                      </RemoveImageButton>
                    )}
                  </ImagePreview>
                  );
                })}
            </ImagePreviewContainer>
          </FormGroup>
          
          <FormActions>
            <CancelButton type="button" onClick={onClose}>
              {readOnly ? 'Tutup' : 'Batal'}
            </CancelButton>
            {!readOnly && (
              <SubmitButton type="submit" disabled={loading || !imageValidation.hasValidImages}>
                {loading ? (isEdit ? 'Menyimpan...' : 'Mengirim...') : (isEdit ? 'Simpan Perubahan' : 'Kirim Laporan')}
              </SubmitButton>
            )}
          </FormActions>
        </form>
      </FormContent>
    </FormWrapper>
  );
};

const ErrorMessage = styled.div`
  background-color: #feeeee;
  color: #dc3545;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  border-left: 3px solid #dc3545;
  display: flex;
  align-items: center;
  &::before {
    content: "⚠️";
    margin-right: 0.5rem;
  }
`;

const FormWrapper = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  overflow: hidden;
  transition: background-color 0.3s;
`;

const FormHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border);
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  
  ${props => {
    switch(props.status) {
      case 'approved':
        return `
          background-color: #e6f8f5;
          color: #00a389;
        `;
      case 'pending':
        return `
          background-color: #fff3e0;
          color: #ff9800;
        `;
      case 'rejected':
        return `
          background-color: #feeeee;
          color: #dc3545;
        `;
      default:
        return '';
    }
  }}
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: var(--text-light);
  
  &:hover {
    background-color: var(--background-light);
  }
`;

const FormContent = styled.div`
  padding: 1.5rem;
  
  form {
    max-width: 800px;
    margin: 0 auto;
  }
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--text-dark);
  }
  
  input, select, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-family: 'Poppins', sans-serif;
    font-size: 1rem;
    color: var(--text-dark);
    background-color: ${props => props.theme === 'dark' ? '#383838' : '#f5f5f5'};
    
    &:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 2px rgba(43, 93, 161, 0.2);
    }
  }
  
  textarea {
    resize: vertical;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  ${FormGroup} {
    flex: 1;
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

const SubmitButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover:not(:disabled) {
    background-color: var(--primary-light);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: #6c757d;
  }
`;

const CancelButton = styled.button`
  background-color: transparent;
  color: var(--text-dark);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--background-light);
  }
`;

const ImagePreviewContainer = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const ImagePreview = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;

  ${ImagePreview}:hover & {
    opacity: 1;
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    background-color: rgba(128, 128, 128, 0.5);
  }
`;

// Notification Toast styles (copied from InterupsiForm.js for consistency)
const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  min-width: 300px;
  background-color: ${props => props.$type === 'success' ? '#2ecc71' : '#e74c3c'};
  color: white;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 9999;
  animation: slideIn 0.3s ease-out forwards;
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
`;
const NotificationMessage = styled.div`
  flex: 1;
  font-weight: 500;
`;
const CloseNotificationButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  margin-left: 10px;
  opacity: 0.8;
  transition: opacity 0.2s;
  &:hover { opacity: 1; }
`;

const ImageValidationError = styled.div`
  background-color: #feeeee;
  color: #dc3545;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  margin-top: 0.5rem;
  font-size: 0.85rem;
  border-left: 3px solid #dc3545;
  display: flex;
  align-items: center;
  &::before {
    content: "⚠️";
    margin-right: 0.5rem;
  }
`;

const ImageValidationSuccess = styled.div`
  background-color: #e6f8f5;
  color: #00a389;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  margin-top: 0.5rem;
  font-size: 0.85rem;
  border-left: 3px solid #00a389;
  display: flex;
  align-items: center;
  &::before {
    content: "✓";
    margin-right: 0.5rem;
    font-weight: bold;
  }
`;

export default KersosForm;